// JOBE-0044.9: hook for the user-saved-views write side. Owns the
// `GET /api/saved-views` fetch + cache and exposes `create`, `rename`,
// and `remove` mutators. Mutators refetch on success so the rail
// stays in sync without explicit cache surgery.
//
// Status shape mirrors EntityTypesContext / DocumentKindsContext for
// consistency:
//   { status: 'loading' | 'ready' | 'error', views: [...], error: null | string }

import { useCallback, useEffect, useState } from 'react';

export function useUserSavedViews({ enabled = true } = {}) {
  const [state, setState] = useState({
    status: 'loading',
    views: [],
    error: null,
  });

  const refetch = useCallback(async (signal) => {
    setState((s) => ({ ...s, status: 'loading', error: null }));
    try {
      const res = await fetch('/api/saved-views', {
        credentials: 'same-origin',
        signal,
      });
      if (!res.ok) {
        const msg = `http_${res.status}`;
        setState({ status: 'error', views: [], error: msg });
        return;
      }
      const body = await res.json();
      const views = Array.isArray(body?.savedViews) ? body.savedViews : [];
      setState({ status: 'ready', views, error: null });
    } catch (err) {
      if (err?.name === 'AbortError') return;
      setState({ status: 'error', views: [], error: err?.message || String(err) });
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const ctrl = new AbortController();
    refetch(ctrl.signal);
    return () => ctrl.abort();
  }, [enabled, refetch]);

  const create = useCallback(async ({ name, description, query }) => {
    const res = await fetch('/api/saved-views', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ name, description: description || '', query }),
    });
    if (!res.ok) {
      const body = await safeJson(res);
      const msg = body?.message || `http_${res.status}`;
      throw new Error(msg);
    }
    const row = await res.json();
    await refetch();
    return row;
  }, [refetch]);

  const rename = useCallback(async (slug, { name, description, query }) => {
    const patch = {};
    if (typeof name === 'string') patch.name = name;
    if (typeof description === 'string') patch.description = description;
    if (typeof query === 'string') patch.query = query;
    const res = await fetch(`/api/saved-views/${encodeURIComponent(slug)}`, {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (!res.ok) {
      const body = await safeJson(res);
      const msg = body?.message || `http_${res.status}`;
      throw new Error(msg);
    }
    const row = await res.json();
    await refetch();
    return row;
  }, [refetch]);

  const remove = useCallback(async (slug) => {
    const res = await fetch(`/api/saved-views/${encodeURIComponent(slug)}`, {
      method: 'DELETE',
      credentials: 'same-origin',
    });
    if (!res.ok && res.status !== 204) {
      const body = await safeJson(res);
      const msg = body?.message || `http_${res.status}`;
      throw new Error(msg);
    }
    await refetch();
  }, [refetch]);

  return { ...state, refetch, create, rename, remove };
}

async function safeJson(res) {
  try {
    return await res.json();
  } catch (_) {
    return null;
  }
}
